import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import type { Product, ProductQuota } from '../lib/types'
import { mcpBalanceForecast, trialBalanceForecast, type ForecastMonth } from '../lib/forecast'
import { VolumeChart, type ChartSeries } from './VolumeChart'

/** The Subscription page's balance chart (MS-820):
 * - Mambu MCP Contracted — "Invocations Balance" (prototype `renderMcpBalance`):
 *   this month at its full budget, Consumed / Contracted / On Demand Volume,
 *   then each later month's allowance, from the purchases-derived balance
 *   (Q11) and the real On Demand renewal windows.
 * - Trial, both products (prototype `renderTrialBalance`): Consumed / Trial
 *   Volume this month, then twelve empty columns — a trial allowance does not
 *   renew, and the empty months are what a contract would fill.
 *
 * Mambu Agents Contracted has its own card, AgentsCreditsBalanceCard. */
export function BalanceForecastChart({
  product,
  quota,
  trial,
}: Readonly<{ product: Product; quota: ProductQuota; trial: boolean }>) {
  const { t } = useTranslation()
  const months = useMemo(() => {
    const today = new Date()
    return trial ? trialBalanceForecast(quota, today) : mcpBalanceForecast(quota, today)
  }, [quota, trial])

  const consumed: ChartSeries = { key: 'consumed', label: t('subscription.balance.consumed'), tone: 'consumed' }
  const series: ChartSeries[] = trial
    ? [consumed, { key: 'trial', label: t('subscription.balance.trialVolume'), tone: 'contracted' }]
    : [
        consumed,
        { key: 'contracted', label: t('subscription.balance.contracted'), tone: 'contracted' },
        { key: 'onDemand', label: t('subscription.balance.onDemand'), tone: 'onDemand' },
      ]

  const isAgents = product === 'Mambu Agents'
  return (
    <VolumeChart
      months={months}
      series={series}
      legendTitle={t(isAgents ? 'subscription.creditsBalance.legendTitle' : 'subscription.balance.legendTitle')}
      yTitle={t(isAgents ? 'subscription.creditsBalance.axisTitle' : 'subscription.balance.axisTitle')}
      totalLabel={(m: ForecastMonth) => {
        if (isAgents) return t('subscription.creditsBalance.total')
        return t((m.values.consumed ?? 0) > 0 ? 'subscription.balance.monthlyVolume' : 'subscription.balance.available')
      }}
    />
  )
}
